import { declareParams, JsonApiRequestError } from '@farfetched/core';
import { zodContract } from '@farfetched/zod';
import { RegistryParams, RegistryQuery } from 'shared/lib';
import { createAuthQuery } from '../auth-http-client';
import {
	createPaginatedSchema,
	accountReportSchema,
	AccountReportWithId,
	accountReportAdditionalDataSchema,
	AccountReportAdditionalData,
} from '../types';

export type OriginalAccountReportRegistryParams = {
	sortField?: string | null;
	sortDirection?: string;
	page?: number;
	limit?: number;
	currencies?: string[];
	accountIds?: string[];
	dateFrom?: string;
	dateTo?: string;
	timeZone?: number;
};

export type AccountReportRegistryFilters = {
	currencies: string[];
	accountIds: number[];
	dateFrom: string;
	dateTo: string;
	timeZone: number;
};

export type AccountReportRegistryParams = RegistryParams<AccountReportRegistryFilters>;

export type AccountReportRegistryQuery = RegistryQuery<
	AccountReportWithId,
	AccountReportRegistryFilters,
	JsonApiRequestError,
	AccountReportAdditionalData
>;

export const createAccountReportRegistryQuery = (): AccountReportRegistryQuery => {
	const query = createAuthQuery({
		params: declareParams<AccountReportRegistryParams>(),
		request: {
			method: 'GET',
			url: '/report/account',
			query: (params) => ({
				page: params.page,
				limit: params.limit,
				sortField: params.sortField ?? undefined,
				sortDirection: params.sortDirection,
				currencies: params.filters.currencies?.length === 0 ? undefined : params.filters.currencies,
				accountIds: [...params.filters.accountIds.map(String)],
				dateFrom: params.filters.dateFrom || undefined,
				dateTo: params.filters.dateTo || undefined,
				timeZone: params.filters.timeZone,
			}),
		},
		response: {
			contract: zodContract(
				createPaginatedSchema(accountReportSchema, accountReportAdditionalDataSchema),
			),
			mapData: ({ result }) => ({
				...result,
				rows: result.rows.map<AccountReportWithId>((row) => ({
					...row,
					id: `${row.account.id}-${row.currency}`,
				})),
			}),
		},
	});

	return query;
};
